import type { FamRole } from "../lib/types";

interface MemberRoleSelectProps {
	value: FamRole;
	onChange: (role: FamRole) => void;
	disabled?: boolean;
	// Whether to expose the "pm" option. The PM-transfer flow is intentionally
	// out of scope for Phase 2 settings — a Fam always has exactly one PM, and
	// promoting another member would require demoting the current PM in the
	// same transaction. We hide "pm" here and let the upstream guard apply.
	allowPm?: boolean;
}

const ROLE_LABELS: Record<FamRole, string> = {
	pm: "PM",
	rm: "RM",
	trader: "Trader",
	analyst: "Analyst",
	member: "Member",
};

// MemberRoleSelect — small <select> dropdown for the Fam settings members
// table. Renders the five FamRole options; defaults to hiding "pm" (no
// PM-transfer UI in Phase 2). The disabled prop is set to true while a
// mutation is in flight to prevent double-clicks.
export function MemberRoleSelect({
	value,
	onChange,
	disabled = false,
	allowPm = false,
}: MemberRoleSelectProps) {
	const options: FamRole[] = allowPm
		? ["pm", "rm", "trader", "analyst", "member"]
		: ["rm", "trader", "analyst", "member"];

	return (
		<select
			value={value}
			disabled={disabled}
			onChange={(e) => onChange(e.target.value as FamRole)}
			className="rounded-md border border-[var(--color-border)] bg-[var(--color-bg-elev)] px-2 py-1 text-xs text-[var(--color-fg)] hover:border-[var(--color-accent)] disabled:cursor-not-allowed disabled:opacity-50"
		>
			{value === "pm" && !options.includes("pm") && (
				<option value="pm">{ROLE_LABELS.pm}</option>
			)}
			{options.map((r) => (
				<option key={r} value={r}>
					{ROLE_LABELS[r]}
				</option>
			))}
		</select>
	);
}
